<template>
  <div
    v-if="totalPaginas > 0"
    class="d-flex"
    :class="esMovil ? 'justify-content-center' : 'justify-content-end'"
  >
    <ul class="pagination m-3 pagination-primary">
      <li class="page-item" v-if="pagina > 1">
        <a class="page-link" aria-label="First" title="Primera página" @click.prevent="ir(1)">
          <span aria-hidden="true">&laquo;&laquo;</span>
        </a>
      </li>
      <li class="page-item" :class="{ disabled: pagina === 1 || deshabilitado }">
        <a class="page-link" aria-label="Previous" title="Anterior" @click.prevent="ir(pagina - 1)">
          Anterior
        </a>
      </li>
      <template v-if="esMovil">
        <li class="page-item active">
          <span class="page-link">{{ pagina }}</span>
        </li>
      </template>
      <template v-else>
        <template v-for="(p, i) in paginasVisibles" :key="p === '...' ? `e${i}` : p">
          <li v-if="p !== '...'" class="page-item" :class="{ active: p === pagina }">
            <a class="page-link" @click.prevent="ir(p)">{{ p }}</a>
          </li>
          <li v-else class="page-item disabled">
            <span class="page-link">...</span>
          </li>
        </template>
      </template>
      <li class="page-item" :class="{ disabled: pagina === totalPaginas || deshabilitado }">
        <a class="page-link" aria-label="Next" title="Siguiente" @click.prevent="ir(pagina + 1)">
          Siguiente
        </a>
      </li>
      <li class="page-item" v-if="pagina < totalPaginas">
        <a class="page-link" aria-label="Last" title="Última página" @click.prevent="ir(totalPaginas)">
          <span aria-hidden="true">&raquo;&raquo;</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "PaginacionTabla",
  props: {
    pagina: { type: Number, required: true },
    totalPaginas: { type: Number, required: true },
    deshabilitado: { type: Boolean, default: false },
  },
  emits: ["cambiar"],
  data() {
    return {
      anchoVentana: window.innerWidth,
    };
  },
  computed: {
    esMovil() {
      return this.anchoVentana < 768;
    },
    /** Primera página, la actual con sus vecinas y la última, con "..." en los saltos. */
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.pagina;

      if (total <= 5) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const numeros = new Set();
      if (actual === 1) {
        numeros.add(1).add(2).add(3).add(total);
      } else if (actual === 2) {
        numeros.add(1).add(2).add(3).add(4).add(total);
      } else if (actual === total) {
        numeros.add(total - 3).add(total - 2).add(total - 1).add(total);
      } else {
        numeros.add(1).add(actual - 1).add(actual).add(actual + 1).add(total);
      }

      const ordenadas = Array.from(numeros).sort((a, b) => a - b);
      const resultado = [];
      ordenadas.forEach((p, i) => {
        if (i > 0 && p - ordenadas[i - 1] > 1) resultado.push("...");
        resultado.push(p);
      });
      return resultado;
    },
  },
  mounted() {
    window.addEventListener("resize", this.alRedimensionar);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.alRedimensionar);
  },
  methods: {
    alRedimensionar() {
      this.anchoVentana = window.innerWidth;
    },
    ir(p) {
      if (this.deshabilitado || p < 1 || p > this.totalPaginas || p === this.pagina) return;
      this.$emit("cambiar", p);
    },
  },
};
</script>

<style scoped>
.pagination {
  flex-wrap: wrap;
  gap: 0;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  display: inline-flex;
  overflow: hidden;
  padding: 0;
}

.pagination .page-item {
  margin: 0;
  border-right: 1px solid #e9ecef;
}

.pagination .page-item:last-child {
  border-right: none;
}

.pagination .page-link {
  border: none;
  padding: 0.5rem 0.75rem;
  margin: 0;
  color: #5c61f2;
  background-color: #fff;
  border-radius: 0;
  position: relative;
  min-width: 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination .page-item.active .page-link {
  background-color: #5c61f2;
  color: #fff;
  border-color: #5c61f2;
  z-index: 1;
  font-weight: 500;
}

.pagination .page-item.disabled .page-link {
  color: #adb5bd;
  pointer-events: none;
  background-color: #fff;
  opacity: 0.7;
  cursor: not-allowed;
}

.pagination .page-link:hover:not(.disabled):not(.active) {
  background-color: #f8f9fa;
  color: #5c61f2;
}

.pagination .page-link:focus {
  box-shadow: none;
  outline: none;
}

.pagination .page-link[aria-label="Next"],
.pagination .page-link[aria-label="Previous"] {
  padding: 0.5rem 1rem;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .pagination {
    justify-content: center !important;
    width: 100%;
    margin: 1rem auto !important;
  }

  .pagination .page-link {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    min-width: auto;
  }

  .pagination .page-link[aria-label="First"],
  .pagination .page-link[aria-label="Last"] {
    padding: 0.5rem 0.75rem;
  }

  .pagination .page-item.active .page-link {
    min-width: 2.5rem;
    font-weight: 600;
  }
}
</style>
